import React, { useState, useEffect } from "react";
import { Drawer, IconButton, Avatar } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import "./HowThisCertificationHelp.css";
import useWindowResize from "../../../utils/WindowResize";

const HowThisCertificationHelp = ({ open, onClose, onButtonClick }) => {
  //   const [open, setOpen] = useState(false);
  const isMobile = useWindowResize();

  const benefits = [
    {
      heading: "Stand Out in ATS Systems",
      subtext:
        "Your certification gets parsed and highlighted by applicant tracking systems, putting you ahead of non-certified candidates.",
    },
    {
      heading: "Verified Skills Badge",
      subtext:
        "Showcase project-based validation that proves you can deliver real solutions, not just pass multiple-choice tests.",
    },
    {
      heading: "LinkedIn Profile Boost",
      subtext:
        "Add a credential that recruiters actively search for when sourcing candidates for technical roles.",
    },
    {
      heading: "Fast-Track Interviews",
      subtext:
        "Certified candidates are 3x more likely to receive interview requests from hiring managers.",
    },
  ];

  const testimonials = [
    {
      review:
        "This certification helped me land my dream job at Google. Recruiters specifically mentioned my verified skills during the interview process.",
      name: "Advait Kole",
      position: "Software Engineer 1 @Google",
      avatar: "AK",
    },
    {
      review:
        "The project-based approach made my resume stand out. I got interview calls from 5 companies within 2 weeks of adding this certification.",
      name: "Priya Sharma",
      position: "Frontend Developer @Microsoft",
      avatar: "PS",
    },
    {
      review:
        "Recruiters love seeing practical skills validation. This certification opened doors that were previously closed to me.",
      name: "Rahul Verma",
      position: "Full Stack Engineer @Amazon",
      avatar: "RV",
    },
  ];

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      PaperProps={{
        className: "howThisCertificationHelp-drawer",
        component: motion.div,
        initial: isMobile ? { y: "100%" } : { x: "100%" },
        animate: isMobile ? { y: 0 } : { x: 0 },
        exit: isMobile ? { y: "100%" } : { x: "100%" },
        transition: { type: "spring", damping: 25, stiffness: 200 },
      }}
    >
      <div className="howThisCertificationHelp-container">
        {/* Fixed Header with Close Button */}
        <div className="howThisCertificationHelp-header">
          <IconButton
            onClick={onClose}
            className="howThisCertificationHelp-closeBtn"
          >
            <CloseIcon />
          </IconButton>
        </div>

        {/* Scrollable Content */}
        <div className="howThisCertificationHelp-content">
          {/* Title Section */}
          <motion.h2
            className="howThisCertificationHelp-title"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            How This Certification Helps You Get Noticed by Recruiters
          </motion.h2>

          {/* Benefits Section */}
          <div className="howThisCertificationHelp-benefits">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="howThisCertificationHelp-benefitCard"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
              >
                <div className="howThisCertificationHelp-benefitIcon">
                  <CheckCircleIcon />
                </div>
                <div className="howThisCertificationHelp-benefitText">
                  <h3 className="howThisCertificationHelp-benefitHeading">
                    {benefit.heading}
                  </h3>
                  <p className="howThisCertificationHelp-benefitSubtext">
                    {benefit.subtext}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Testimonials Section */}
          <motion.div
            className="howThisCertificationHelp-testimonials"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <Swiper
              modules={[Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              loop={true}
              className="howThisCertificationHelp-swiper"
            >
              {testimonials.map((testimonial, index) => (
                <SwiperSlide key={index}>
                  <div className="howThisCertificationHelp-testimonialCard">
                    <p className="howThisCertificationHelp-review">
                      "{testimonial.review}"
                    </p>
                    <div className="howThisCertificationHelp-testimonialAuthor">
                      <Avatar className="howThisCertificationHelp-avatar">
                        {testimonial.avatar}
                      </Avatar>

                      <div className="howThisCertificationHelp-authorInfo">
                        <p className="howThisCertificationHelp-authorName">
                          {testimonial.name}
                        </p>
                        <p className="howThisCertificationHelp-authorPosition">
                          {testimonial.position}
                        </p>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        </div>

        {/* Fixed Footer with CTA Button */}
        <div className="howThisCertificationHelp-footer">
          <motion.button
            className="howThisCertificationHelp-ctaButton"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onButtonClick}
          >
            <span className="howThisCertificationHelp-shimmer"></span>
            Get My Certificate
          </motion.button>
        </div>
      </div>
    </Drawer>
  );
};

export default HowThisCertificationHelp;
